import type { WireCompanyRelated } from '@/api/hooks';
import type { WireCompany, WireContact, WireEmployment, WireFolder, WireLink, WireThread } from '@/api/wire';
import type { ItemContext, Party } from '@/ui/context';
import type { Who } from '@/ui/who';
import { countryName, humanise, stateWords } from '@/ui/words';
import { agoWords, dayOf, daysSince, nowOf, originOf, others, personParty, plural, relationshipSentence, type Viewer } from './explainKit';

/**
 * What the directory's records say about themselves (ADR-0155): companies,
 * contacts, the employments between them, and what a company's page lists
 * under it. Each builder reads only the row the screen already holds; where
 * the wire carries no source or no owner the panel says "not recorded" or
 * leaves the line out, and never guesses one.
 *
 * The rule that shapes every tip is ADR-0063/0064: being in the directory
 * means we know a party exists, not that we do business with them. A person
 * approves a relationship, under a one-time code a superadmin mints, and
 * until then a twin will not work with them and a department agent works
 * with them only inside an assignment that says so.
 */

/** Quiet is the Insight page's own threshold: thirty days with no mail in or out. */
export const QUIET_DAYS = 30;

function approver(c: Pick<WireCompany, 'relationshipApprovedByName' | 'relationshipApprovedBy'>, who: Who, v: Viewer): Party | null {
  return personParty(v, who, c.relationshipApprovedBy, c.relationshipApprovedByName, 'approved the relationship');
}

/** The sentence about what the relationship is and who decided it. */
function relationshipLine(c: WireCompany | WireContact, who: Who, v: Viewer): string {
  if (c.relationship === 'unapproved') return 'Nobody has said we do business with them.';
  const by = approver(c, who, v);
  const on = dayOf(c.relationshipApprovedAt);
  return `Approved by ${by ? (by.name === 'You' ? 'you' : by.name) : 'a person'}${on ? ` on ${on}` : ''} as ${relationshipSentence(c.relationship)}.`;
}

// --- companies ----------------------------------------------------------------------

export interface CompanyExtra {
  /** On the company's own page: no link back to itself, and "above" for the buttons. */
  onPage?: boolean | undefined;
  /** Whether details were written down for it (known only on its page). */
  details?: boolean | null | undefined;
  /** How many prospects of this department it is (known only on its page). */
  prospects?: number | null | undefined;
}

export function explainCompany(c: WireCompany, who: Who, v: Viewer, extra: CompanyExtra = {}): ItemContext {
  const name = c.displayName ?? c.legalName;
  const now = nowOf(v);
  const where = extra.onPage ? 'with the buttons above' : 'from its page';
  const unapproved = c.relationship === 'unapproved';
  const people = `${plural(c.peopleNow, 'person', 'people')} work${c.peopleNow === 1 ? 's' : ''} there now`;
  const closed = (c.employments ?? []).filter((e) => !e.current).length;
  const by = approver(c, who, v);
  const came = originOf(c.origin, who, v);

  const next = ((): ItemContext['next'] => {
    if (unapproved) {
      return {
        tip: v.isSuperadmin
          ? `Decide what ${name} is to us. Approving a company approves everyone employed there today, so look at who works there first, then choose client, vendor, partner or investor ${where}.`
          : `${name} is only known, not approved. If your work needs them, ask a superadmin to approve them: it is a person's decision, taken with a one-time code. Your twin cannot work with them until then.`,
        instead: 'Or leave it as it is. Mail from them still arrives and shows in the daily digest, and nothing is lost by waiting.',
      };
    }
    if (c.relationship === 'internal') {
      return { tip: 'Nothing is needed from you. This is us in another guise, kept so our own mail files correctly.', instead: `If it is wrong, change the relationship ${where}; that takes a superadmin's one-time code.` };
    }
    if (extra.details === false) {
      return {
        tip: `Write down what you can read on its website or national register (address, phone, organisation number) ${where}. Everyone who works with ${name} sees it, in every department.`,
        instead: 'Or leave it: nothing depends on the details, but the next person will have to look them up again.',
      };
    }
    if (c.peopleNow === 0) {
      return {
        tip: `Nobody is recorded as working at ${name} now. If you know who deals with them, move a contact to it from that contact's page; otherwise ask whoever last wrote to them.`,
        instead: 'Or leave it: a company with no one here is still the right place for its papers and mail.',
      };
    }
    return {
      tip: `Nothing is needed from you. ${name} is on file and its people are listed; open it when mail from them arrives, or to find who to write to.`,
      instead: `If the relationship has ended, change it ${where}. That takes a superadmin's one-time code, and nothing may write to them again until someone approves them again.`,
    };
  })();

  const prospects = extra.prospects ?? null;
  return {
    subject: { kind: 'company', title: name },
    summary: unapproved
      ? `${name} is a company on file. ${relationshipLine(c, who, v)} ${people.charAt(0).toUpperCase()}${people.slice(1)}.`
      : `${name} is ${relationshipSentence(c.relationship)}. ${people.charAt(0).toUpperCase()}${people.slice(1)}.`,
    need: unapproved
      ? 'A person to decide what they are to us. Until then a twin will not touch them, and a department agent works with them only inside an assignment that says so.'
      : extra.details === false
        ? 'Its details written down: address, phone, organisation number. Nothing else.'
        : 'Nothing, unless its details or its people are out of date.',
    why: unapproved
      ? `Companies are the whole company's own, so every department reads them. ${v.isSuperadmin ? 'You approve counterparties, so this one waits on you.' : 'Approving is a superadmin’s act.'}`
      : 'Companies are the whole company’s own, so every department reads this one; you see it because mail, a prospect or a person names it.',
    // The core keeps a name and a domain; how it got on file is kept beside it (ADR-0165).
    source:
      came.source ??
      (prospects !== null && prospects > 0 ? { label: `A prospecting plan found it; it is now ${plural(prospects, 'prospect')} of this department` } : came.source),
    broughtBy: came.broughtBy,
    knows: others([by, ...who.knowers(v.department)]),
    facts: [
      ['What it is to us', unapproved ? 'Unapproved' : humanise(c.relationship)],
      ['Domain', c.domain],
      ['People there now', String(c.peopleNow)],
      ['Worked there before', closed > 0 ? String(closed) : null],
      ['On file since', dayOf(c.createdAt)],
      ['Brought by', came.department],
      ['Last changed', agoWords(c.updatedAt, now)],
    ],
    next,
    ...(extra.onPage ? {} : { open: { to: `/companies/${c.id}` } }),
    ...(unapproved ? { task: { title: v.isSuperadmin ? `Decide what ${name} is to us` : `Ask for ${name} to be approved`, from: `/companies/${c.id}` } } : {}),
  };
}

// --- contacts -----------------------------------------------------------------------

export interface ContactExtra {
  onPage?: boolean | undefined;
}

/** The later of the last mail in and the last mail out: when we last were in touch. */
export function lastTouch(c: Pick<WireContact, 'lastInboundAt' | 'lastOutboundAt'>): string | null {
  const at = [c.lastInboundAt, c.lastOutboundAt].filter((x): x is string => !!x);
  if (at.length === 0) return null;
  return at.reduce((a, b) => (Date.parse(a) >= Date.parse(b) ? a : b));
}

export function explainContact(c: WireContact, who: Who, v: Viewer, extra: ContactExtra = {}): ItemContext {
  const now = nowOf(v);
  const where = extra.onPage ? 'with the buttons above' : 'from their page';
  const unapproved = c.relationship === 'unapproved';
  const owner = personParty(v, who, c.ownerHuman, c.ownerName, 'holds this relationship');
  const mine = c.ownerHuman !== null && c.ownerHuman === v.humanId;
  const heldByOther = owner !== null && !mine;
  const last = lastTouch(c);
  const sinceTouch = daysSince(last, now);
  const onFile = daysSince(c.createdAt, now);
  const quiet = sinceTouch !== null ? sinceTouch >= QUIET_DAYS : onFile !== null && onFile >= QUIET_DAYS;
  const job = c.currentCompanyName ? (c.currentRole ? `is ${c.currentRole} at ${c.currentCompanyName}` : `works at ${c.currentCompanyName}`) : 'has no current employment on record';
  const first = c.fullName.split(/\s+/)[0] ?? c.fullName;
  const by = approver(c, who, v);
  const came = originOf(c.origin, who, v);

  const next = ((): ItemContext['next'] => {
    if (unapproved) {
      return {
        tip: v.isSuperadmin
          ? `Decide whether we do business with ${first}. Approving a contact approves that person only, not their colleagues; choose what they are ${where}.`
          : `${first} is only known, not approved. If your work needs them, ask a superadmin to approve them: a person's decision, taken with a one-time code. Your twin cannot draft to them until then.`,
        instead: 'Or leave it. What they write still arrives and shows in the daily digest; it does not become work until someone approves them.',
      };
    }
    if (heldByOther && quiet) {
      return {
        tip: `${owner!.name} holds this relationship and it has been quiet${sinceTouch !== null ? ` for ${plural(sinceTouch, 'day')}` : ''}. Ask ${owner!.name} whether to get back in touch before anyone writes.`,
        instead: 'Or leave it with them: nothing is lost, and they will see it on their own list.',
      };
    }
    if (quiet) {
      return {
        tip:
          sinceTouch === null
            ? `Nobody has written to ${first} or heard from them in the ${plural(onFile ?? 0, 'day')} they have been on file. Decide whether they are still wanted, and if so ask your twin to draft a first note: you read it and you send it.`
            : `Quiet for ${plural(sinceTouch, 'day')}. Ask your twin to draft a short note to ${first}; you read it and you send it.`,
        instead: c.currentCompanyName ? 'Or, if they have left, move them to their new company from their page so both companies keep a clean history.' : 'Or, if they have moved on, add where they work now from their page.',
      };
    }
    if (!c.currentCompanyName) {
      return {
        tip: `${first} has no current employer on record. If you know where they work now, use Move company ${where}.`,
        instead: 'Or leave it: they can stay on file without one.',
      };
    }
    return {
      tip: `Nothing is needed from you. ${first} was last in touch ${agoWords(last, now) ?? 'recently'}${heldByOther ? `, and ${owner!.name} holds the relationship` : ''}.`,
      instead: mine ? 'If someone else should hold this relationship, hand them over from their page.' : 'If they ask to be forgotten, record it from their page: their address is suppressed at once and a superadmin carries it out.',
    };
  })();

  return {
    subject: { kind: 'contact', title: c.fullName },
    summary: `${c.fullName} ${job}. ${relationshipLine(c, who, v)}`,
    need: unapproved
      ? 'A person to decide whether we do business with them. Until then your twin cannot draft to them or put them on a task.'
      : quiet
        ? 'A reason to get back in touch, or a note that they have moved on.'
        : 'Nothing: the relationship is approved and recent.',
    why: mine
      ? 'You hold this relationship, so replies and follow-ups are yours.'
      : heldByOther
        ? `${owner!.name} holds this relationship. You see it because contacts are the company’s own and every department reads them.`
        : 'Nobody holds this relationship yet. Contacts are the company’s own, so every department reads them.',
    // The core keeps who they are and when; how they arrived is kept beside it (ADR-0165).
    source: came.source,
    broughtBy: came.broughtBy,
    owner,
    knows: others([owner, by, ...who.knowers(v.department)]),
    facts: [
      ['Works at', c.currentCompanyName],
      ['Role', c.currentRole],
      ['Last heard from', c.lastInboundAt ? agoWords(c.lastInboundAt, now) : 'Not yet'],
      ['Last written to', c.lastOutboundAt ? agoWords(c.lastOutboundAt, now) : 'Not yet'],
      ['Worked at', (c.employments ?? []).length > 1 ? plural((c.employments ?? []).length, 'company', 'companies') : null],
      ['On file since', dayOf(c.createdAt)],
      ['Brought by', came.department],
    ],
    next,
    ...(extra.onPage ? {} : { open: { to: `/contacts/${c.id}` } }),
    ...(unapproved
      ? { task: { title: v.isSuperadmin ? `Decide whether to approve ${c.fullName}` : `Ask for ${c.fullName} to be approved`, from: `/contacts/${c.id}` } }
      : quiet && !heldByOther
        ? { task: { title: `Get back in touch with ${c.fullName}`, from: `/contacts/${c.id}` } }
        : {}),
  };
}

// --- employments: who works where ---------------------------------------------------

/**
 * One employment. `shows` is which end the row names, as the page's own
 * timeline does: a contact's page shows each company they worked at
 * (`company`), a company's page shows each person who worked there (`contact`).
 */
export function explainEmployment(e: WireEmployment, shows: 'company' | 'contact', who: Who, v: Viewer): ItemContext {
  const person = e.contactName;
  const title = shows === 'contact' ? `${person}, ${e.roleTitle ?? 'role not recorded'}` : `${e.roleTitle ?? 'Role not recorded'} at ${e.companyName}`;
  const span = e.validTo ? `from ${dayOf(e.validFrom)} to ${dayOf(e.validTo)}` : `since ${dayOf(e.validFrom)}`;
  const to = shows === 'contact' ? `/contacts/${e.contactId}` : `/companies/${e.companyId}`;
  return {
    subject: { kind: 'employment', title },
    summary: `${person} ${e.current ? 'works' : 'worked'} at ${e.companyName}${e.roleTitle ? ` as ${e.roleTitle}` : ''}, ${span}. ${
      e.current ? 'It is their current job.' : 'It has closed; they moved on and the link was kept, not erased.'
    }`,
    need: e.current ? 'Nothing, unless it is out of date.' : 'Nothing: a closed link is history.',
    why: 'Each employment is its own link with a start and an end, so both companies keep a clean history when someone moves.',
    source: null,
    broughtBy: null,
    knows: others(who.knowers(v.department)),
    facts: [
      ['Started', dayOf(e.validFrom)],
      ['Ended', dayOf(e.validTo)],
    ],
    next: e.current
      ? {
          tip: shows === 'contact' ? `Nothing is needed from you. If ${person} has left ${e.companyName}, open their page and use Move company so this one closes.` : 'Nothing is needed from you. If you have left that job, use Move company above so this link closes and a new one opens.',
          instead: 'Moving someone never deletes the old link, so nothing is lost either way.',
        }
      : { tip: 'Nothing to do. This job is over and stays in their history.', instead: 'If it was recorded wrongly, ask a colleague who knows them before changing it: a move is recorded with a date, not edited away.' },
    open: { to },
  };
}

// --- what a company's page lists under it -------------------------------------------

type Related = WireCompanyRelated;

export function explainRelatedProspect(p: Related['prospects'][number], who: Who, v: Viewer, company: string): ItemContext {
  const title = p.name ?? 'A prospect with no name written down yet';
  const fit = p.fitScore !== null ? `Its fit score is ${p.fitScore}.` : 'It has no fit score yet.';
  return {
    subject: { kind: 'prospect', title },
    summary: `${title} is a prospect of this department in ${countryName(p.marketCode)}: ${company} as a business our plans found. It stands at “${stateWords(p.stage).toLowerCase()}”. ${fit}`,
    need: p.stage === 'excluded' ? 'Nothing: it was left out.' : 'A person to read what was found and decide whether to approach them.',
    why: `${company} is the same business as this prospect, so the department’s own work on it is listed here.`,
    source: { label: 'A prospecting plan of this department found it', to: `/prospects/${p.id}` },
    broughtBy: null,
    knows: others(who.knowers(v.department)),
    next:
      p.stage === 'excluded'
        ? { tip: 'Nothing to do. It was excluded, and its page says why.', instead: 'Open it only if you think the exclusion was wrong.' }
        : { tip: 'Open the prospect to read what was found and what the plan drafted. Nothing is sent: every first email is a person’s to read and send.', instead: 'Or leave it; a prospect that is never approached costs nothing.' },
    open: { to: `/prospects/${p.id}` },
  };
}

export function explainRelatedBuyer(b: Related['buyers'][number], who: Who, v: Viewer, company: string): ItemContext {
  return {
    subject: { kind: 'buyer', title: b.name },
    summary: `${b.name} is a buyer of this department in ${countryName(b.marketCode)}, and the same business as ${company}.`,
    need: 'Nothing unless you are working the account.',
    why: `${company} is on file as this buyer, so what the department holds about it is listed here.`,
    source: null,
    broughtBy: null,
    knows: others(who.knowers(v.department)),
    next: { tip: 'Open the buyer to see its orders, samples and the last thing said. Nothing is needed from you if you are not working this account.', instead: 'Or ask the department’s lead who holds it.' },
    open: { to: `/buyers/${b.id}` },
  };
}

export function explainRelatedSupplier(s: Related['suppliers'][number], who: Who, v: Viewer, company: string): ItemContext {
  return {
    subject: { kind: 'supplier', title: s.name },
    summary: `${s.name} (${countryName(s.country)}) is ${s.role === 'current' ? 'our current supplier' : 'an alternative supplier we are keeping in view'}, and the same business as ${company}.`,
    need: s.role === 'current' ? 'Nothing unless a price or a delivery is in question.' : 'Nothing: it is a fall-back until someone needs it.',
    why: `${company} is on file as this supplier, so the department’s validation of it is listed here.`,
    source: null,
    broughtBy: null,
    knows: others(who.knowers(v.department)),
    next: {
      tip: s.role === 'current' ? 'Open the supplier to see what has been validated and what the last answer was.' : 'Open the supplier if the current one fails: what was validated about it is kept there.',
      instead: 'Anything a supplier says about price or availability counts only once it is in writing.',
    },
    open: { to: `/suppliers/${s.id}` },
  };
}

export function explainRelatedSignal(g: Related['signals'][number], who: Who, v: Viewer, company: string): ItemContext {
  return {
    subject: { kind: 'signal', title: g.said.slice(0, 80) },
    summary: `Something noticed about ${company}: “${g.said}”. It is a ${humanise(g.kind).toLowerCase()} signal, seen ${dayOf(g.seenOn) ?? 'on a date not recorded'}.`,
    need: 'A person to judge whether it changes anything. A signal carries no points on its own.',
    why: `It names ${company} and was kept with its source, so it appears under the company.`,
    source: g.source ? { label: g.source, when: dayOf(g.seenOn) ?? undefined } : null,
    broughtBy: null,
    knows: others(who.knowers(v.department)),
    next: { tip: 'Read it, and if it matters write the next step on the company or its prospect. Nothing is sent because of a signal.', instead: 'Or ignore it: a signal that nobody acts on expires with the rest.' },
  };
}

// --- conversations filed against a record --------------------------------------------

export function explainLinkedThread(t: WireThread, who: Who, v: Viewer): ItemContext {
  const now = nowOf(v);
  const subject = t.subject ?? 'A conversation with no subject';
  const waited = daysSince(t.waitingSince, now);
  const mine = t.claimedByHuman !== null && t.claimedByHuman === v.humanId;
  const owner: Party | null = mine ? { ...who.me, role: 'has claimed it' } : t.claimedByHuman ? { name: t.claimedByName || 'A colleague', kind: 'person', role: 'has claimed it' } : null;
  const next = ((): ItemContext['next'] => {
    if (t.state === 'closed') return { tip: 'Nothing to do. This conversation is closed; open it only to read it again.', instead: 'If they write again it opens by itself.' };
    if (t.needsReply) {
      return {
        tip: `They are waiting for an answer${waited !== null && waited > 0 ? ` (${plural(waited, 'day')} so far)` : ''}. Open the thread to read what they said; your twin can prepare a draft, and you read it and send it.`,
        instead: 'Or close it from the thread if no answer is owed.',
      };
    }
    if (t.state === 'waiting_reply') {
      return {
        tip: `Nothing is needed from you now: we are waiting for their reply${waited !== null ? `, ${plural(waited, 'day')} so far` : ''}.`,
        instead: waited !== null && waited >= 7 ? 'It has been a week or more: open the thread and have a nudge drafted if the answer still matters.' : 'If nothing comes, the thread keeps its place here.',
      };
    }
    if (t.state === 'snoozed') return { tip: 'Nothing is needed until it comes back. It was snoozed on purpose.', instead: 'Open it to bring it back sooner.' };
    return { tip: 'Open the thread to see where it stands; nothing in it is waiting on you.', instead: 'Or leave it: it stays filed here with the record.' };
  })();
  return {
    subject: { kind: 'conversation', title: subject },
    summary: `${subject}: ${plural(t.messageCount, 'message')}${t.lastFrom ? `, the last from ${t.lastFrom.includes('@') ? 'them' : t.lastFrom}` : ''}. ${stateWords(t.needsReply ? 'waiting_us' : t.state)}.`,
    need: t.needsReply ? 'A reply from you or your department.' : t.state === 'waiting_reply' ? 'Their reply; nothing from you.' : 'Nothing right now.',
    why: 'It is filed against this record, so it is here when you look at them.',
    source: { label: 'A conversation in Mail, filed against this record', to: `/mail/${t.id}`, when: dayOf(t.createdAt) ?? undefined },
    broughtBy: null,
    owner,
    involved: t.claimedByAgent ? who.agentsOf(t.department) : undefined,
    knows: others(who.knowers(t.department ?? v.department)),
    facts: [
      ['Last message', agoWords(t.lastMessageAt, now)],
      ['Waiting for an answer since', t.needsReply || t.state === 'waiting_reply' ? agoWords(t.waitingSince, now) : null],
    ],
    next,
    open: { to: `/mail/${t.id}`, label: 'Open the conversation' },
  };
}

const LINKED: Record<string, string> = { thread: 'conversation', message: 'email', deal: 'deal', order: 'order', sample: 'sample', invoice: 'invoice', task: 'task', project: 'project' };

/** An "also linked" row: something else filed against the same record. */
export function explainLink(l: WireLink, who: Who, v: Viewer): ItemContext {
  const kind = LINKED[l.sourceTable] ?? humanise(l.sourceTable).toLowerCase();
  const title = `A ${kind}`;
  const by = who.actor(l.linkedBy);
  const confidence = l.confidence !== null && l.confidence < 1 ? ` The match was a guess at ${Math.round(l.confidence * 100)}%, so check it.` : '';
  return {
    subject: { kind: 'link', title },
    summary: `${title} is filed against this record (${humanise(l.relation).toLowerCase()}), linked ${dayOf(l.linkedAt) ?? 'on a date not recorded'}.${confidence}`,
    need: l.confidence !== null && l.confidence < 1 ? 'A person to confirm the match is right.' : 'Nothing.',
    why: 'Whatever is linked to a record is listed with it, so the whole story is in one place.',
    source: by ? { label: by.kind === 'person' ? 'Linked by a person' : 'Linked by the platform', when: dayOf(l.linkedAt) ?? undefined } : null,
    broughtBy: by ? { ...by, when: dayOf(l.linkedAt) ?? undefined } : null,
    knows: others(who.knowers(v.department)),
    next: {
      tip: l.confidence !== null && l.confidence < 1 ? `Check the match: ask whoever linked it, or open the ${kind} yourself to see that it belongs here.` : `Nothing is needed from you. Find the ${kind} from the department’s own list when you need it.`,
      instead: 'A wrong link does not change the record; it only means the wrong thing is listed here.',
    },
  };
}

// --- the shared drive's folders ------------------------------------------------------

export function explainFolder(f: WireFolder, who: Who, v: Viewer, forCompany: boolean): ItemContext {
  const ours = f.department === v.department;
  const holder = f.department ? who.department(f.department) : null;
  const writers = f.writableBy.map((w) => (w === 'human' ? 'people' : 'agents')).join(' and ');
  return {
    subject: { kind: 'folder', title: f.path },
    summary: `${f.path} in the shared drive: ${f.purpose.replace(/\.$/, '')}.${forCompany ? ' It is this company’s own folder, named for it.' : ''}`,
    need: ours ? 'Nothing: the papers go in as they arrive.' : 'Nothing from you: it belongs to another department.',
    why: f.holds.length > 0 ? 'The taxonomy files papers by what a company is to us, and this folder is where that kind goes.' : 'The taxonomy gives this kind of paper this place.',
    source: null,
    broughtBy: null,
    owner: holder,
    knows: others(f.department ? who.knowers(f.department) : []),
    facts: [
      ['Written by', writers],
      ['Kept', f.retention === 'permanent' ? 'Permanently' : f.retention],
    ],
    next: ours
      ? { tip: 'Press “What is in it” to read the folder, as you, under your own drive consent. Nothing is opened until you do.', instead: 'If a paper belongs here, file it in the drive itself; the platform reads, it does not move your files.' }
      : { tip: `This folder is ${holder?.name ?? 'another department'}’s, so it is named here and never opened. Ask them if you need a paper from it.`, instead: 'A department never reads another department’s papers; a request asks them to share what they choose.' },
  };
}

